import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Pencil, Plus, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useLeaveBalancesQuery, useLeaveTypesQuery, useUpsertLeaveTypeMutation } from '@/client/api/hr/people';
import { hasLeft, type LeaveType } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { DeleteConfirmDialog, EmployeeAvatar, EmptyState, Panel, ProgressBar, RowMenu, StatusBadge } from './components/hr-ui';
import { useEmployees } from './hr-store';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

type TypeForm = { code: string; name: string; daysPerYear: string; paid: boolean };

const TimeOffBalancesPage = () => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const employees = useEmployees();
  const balancesQ = useLeaveBalancesQuery(year);
  const typesQ = useLeaveTypesQuery();
  const upsertType = useUpsertLeaveTypeMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [form, setForm] = useState<TypeForm>({ code: '', name: '', daysPerYear: '', paid: true });
  const [toggling, setToggling] = useState<LeaveType | null>(null);

  const balances = balancesQ.data ?? [];
  const types = typesQ.data ?? [];
  const columns = types.filter((t) => t.status === 'active');
  const people = useMemo(() => employees.filter((e) => !hasLeft(e)), [employees]);
  const balanceFor = (employeeId: string, typeId: string) => balances.find((b) => b.employeeId === employeeId && b.typeId === typeId);

  const stats = useMemo(() => {
    const annual = balances.filter((b) => b.type === 'vacation' && b.allotted);
    const usedPct = annual.length ? annual.reduce((s, b) => s + b.used / (b.allotted as number), 0) / annual.length : 0;
    return [
      { key: 'people', label: 'People tracked', value: people.length },
      { key: 'annual', label: `Avg. annual leave used, ${year}`, value: `${Math.round(usedPct * 100)}%` },
      { key: 'pending', label: 'Days pending a decision', value: balances.reduce((n, b) => n + b.pending, 0) },
      { key: 'low', label: 'Under 3 annual days left', value: annual.filter((b) => (b.remaining ?? 99) < 3).length },
    ];
  }, [balances, people, year]);

  const openType = (t: LeaveType | null) => {
    setEditing(t);
    setForm(
      t
        ? { code: t.code, name: t.name, daysPerYear: t.daysPerYear === null ? '' : String(t.daysPerYear), paid: t.paid }
        : { code: '', name: '', daysPerYear: '', paid: true },
    );
    setTypeOpen(true);
  };

  const saveType = (e: React.FormEvent) => {
    e.preventDefault();
    upsertType.mutate(
      {
        id: editing?.id,
        code: form.code.trim(),
        name: form.name.trim(),
        daysPerYear: form.daysPerYear.trim() === '' ? null : Number(form.daysPerYear),
        paid: form.paid,
      },
      {
        onSuccess: () => {
          toast.success(editing ? 'Leave type updated' : 'Leave type added');
          setTypeOpen(false);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the leave type'),
      },
    );
  };

  const confirmToggle = () => {
    if (!toggling) return;
    const t = toggling;
    upsertType.mutate(
      { id: t.id, status: t.status === 'active' ? 'retired' : 'active' },
      {
        onSuccess: () => toast.success(t.status === 'active' ? `${t.name} retired` : `${t.name} restored`),
        onError: (err) => toast.error((err as Error).message || 'Could not update the leave type'),
      },
    );
    setToggling(null);
  };

  return (
    <HrPageShell
      mock={false}
      title="Time off balances"
      description="Each person's allowance, what is used, and what is waiting for a decision. Working days only."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Time Off' }, { label: 'Balances' }]}
      icon={Scale}
      actions={
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      }
      stats={stats}
    >
      {balancesQ.isError ? (
        <EmptyState label={`Could not load balances: ${(balancesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : balancesQ.isLoading || typesQ.isLoading ? (
        <EmptyState label="Loading balances…" />
      ) : people.length === 0 ? (
        <EmptyState label="No employees to show balances for." />
      ) : (
        <div className="mb-6 overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Employee</th>
                {columns.map((t) => (
                  <th key={t.id} className="px-4 py-3 font-medium">
                    {t.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {people.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar employee={e} size="sm" />
                      <span className="font-medium text-gray-900">{e.name}</span>
                    </div>
                  </td>
                  {columns.map((t) => {
                    const b = balanceFor(e.id, t.id);
                    if (!b) return <td key={t.id} className="px-4 py-3 text-gray-400">—</td>;
                    if (b.allotted === null) {
                      return (
                        <td key={t.id} className="px-4 py-3 text-xs text-gray-500">
                          {b.used} used{b.pending ? ` · ${b.pending} pending` : ''}
                        </td>
                      );
                    }
                    return (
                      <td key={t.id} className="px-4 py-3">
                        <p className="mb-1 text-xs text-gray-500">
                          {b.used} / {b.allotted} used{b.pending ? ` · ${b.pending} pending` : ''}
                        </p>
                        <ProgressBar value={b.allotted ? ((b.used + b.pending) / b.allotted) * 100 : 0} className="w-28" />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Panel
        title="Leave types"
        action={
          <PermissionButton code={PERMISSIONS.hr.manage} size="sm" variant="outline" className="gap-1.5" onClick={() => openType(null)}>
            <Plus className="h-3.5 w-3.5" /> Add type
          </PermissionButton>
        }
      >
        {typesQ.isError ? (
          <p className="text-sm text-red-600">Could not load leave types.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2 font-medium">Name</th>
                <th className="py-2 font-medium">Code</th>
                <th className="py-2 font-medium">Days per year</th>
                <th className="py-2 font-medium">Paid</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {types.map((t) => (
                <tr key={t.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && openType(t)}>
                  <td className="py-2 font-medium text-gray-900">{t.name}</td>
                  <td className="py-2 text-gray-500">{t.code}</td>
                  <td className="py-2">{t.daysPerYear ?? 'No cap'}</td>
                  <td className="py-2">{t.paid ? 'Paid' : 'Unpaid'}</td>
                  <td className="py-2">
                    <StatusBadge
                      label={t.status === 'active' ? 'Active' : 'Retired'}
                      className={t.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <RowMenu
                      label={`Actions for ${t.name}`}
                      items={[
                        { label: 'Edit', icon: Pencil, onSelect: () => openType(t), hidden: !canManage },
                        {
                          label: t.status === 'active' ? 'Retire' : 'Restore',
                          icon: t.status === 'active' ? Archive : ArchiveRestore,
                          onSelect: () => setToggling(t),
                          hidden: !canManage,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add leave type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveType} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Name</span>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Code</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  className={inputClass}
                  placeholder="bereavement"
                />
              </label>
            </div>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Working days per year (empty = no cap)</span>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.daysPerYear}
                onChange={(e) => setForm((f) => ({ ...f, daysPerYear: e.target.value }))}
                className={inputClass}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.paid} onChange={(e) => setForm((f) => ({ ...f, paid: e.target.checked }))} />
              Paid leave (payroll does not deduct it)
            </label>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setTypeOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsertType.isPending}>
                {upsertType.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!toggling}
        onOpenChange={(v) => !v && setToggling(null)}
        title={toggling?.status === 'active' ? `Retire ${toggling?.name}?` : `Restore ${toggling?.name}?`}
        description={
          toggling?.status === 'active'
            ? 'Nobody can request it any more. Requests already made stay as they are.'
            : 'People can request it again.'
        }
        actionLabel={toggling?.status === 'active' ? 'Retire' : 'Restore'}
        danger={toggling?.status === 'active'}
        onConfirm={confirmToggle}
      />
    </HrPageShell>
  );
};

export default TimeOffBalancesPage;
